import {
  ISoccerMatch,
  ISoccerMatchEvent,
  PERIOD_ID,
  PERIOD_ID_STATUS,
} from "../../../types";
import { freshestClockSource, publishClockSnapshot } from "./clockSync";

const pad = (n: number) => String(Math.floor(n)).padStart(2, "0");

// Kickoff-elapsed fallback for live matches we don't have per-match data for.
// Holds at 45:00 / 90:00 until per-match data confirms HT / FT — without
// periodId info we can't distinguish stoppage from break, so the safe move
// is to freeze at the boundary rather than tick past it.
export function kickoffElapsedClock(matchDateTime: string, now: number): string {
  const elapsed = now - new Date(matchDateTime).getTime();
  if (elapsed < 0) return "00:00";
  const min = Math.floor(elapsed / 60_000);
  const sec = Math.floor(elapsed / 1000) % 60;
  if (min >= 90) return "90:00";
  if (min >= 45) return "45:00";
  return `${pad(min)}:${pad(sec)}`;
}

// Latest in-period event timeMin extrapolated forward by wall-elapsed since
// it happened. Cross-period events are skipped so HT break time can't leak
// into the 2nd-half estimate.
function eventsBasedSeconds(
  events: ISoccerMatchEvent[] | undefined,
  currentPeriodId: number,
  now: number,
): number {
  if (!events?.length || currentPeriodId < 0) return 0;
  let bestMin = 0;
  let bestTs = 0;
  for (const e of events) {
    const ePid = (e as { periodId?: number }).periodId;
    if (ePid !== currentPeriodId) continue;
    const m = Number(e.timeMin ?? 0);
    if (m > bestMin) {
      bestMin = m;
      bestTs = new Date(e.timestamp).getTime() || 0;
    }
  }
  if (!bestMin) return 0;
  const elapsed = bestTs ? Math.max(0, Math.floor((now - bestTs) / 1000)) : 0;
  return bestMin * 60 + elapsed;
}

// Period max minutes (regulation end). Stoppage minutes beyond this come from
// the API only — never from local extrapolation.
const PERIOD_MAX_MIN: Partial<Record<PERIOD_ID_STATUS, number>> = {
  [PERIOD_ID_STATUS.FIRST_HALF]: 45,
  [PERIOD_ID_STATUS.SECOND_HALF]: 90,
  [PERIOD_ID_STATUS.EXTRA_TIME_FIRST_HALF]: 105,
  [PERIOD_ID_STATUS.EXTRA_TIME_SECOND_HALF]: 120,
};

// Live match clock.
//
// Behavior:
//   • Break/transition periods → fixed labels (HT, FT, ET HT, ET, PEN, 00:00).
//   • Active periods (1H/2H/ET 1H/ET 2H):
//       – API matchLengthMin > period max  → render `${max}'+${extra}'`
//         (matches commentary's "45'+4'" format; only API reports stoppage).
//       – Otherwise live-tick MM:SS using API base + wall-elapsed since sync,
//         falling back to events-based extrapolation when matchLengthMin
//         stalls. Tick is clamped at the period max so we never cross into
//         stoppage on our own — that requires the API to confirm.
export function liveClock(
  soccerMatch: ISoccerMatch | undefined,
  syncedAt: number | undefined,
  now: number,
): string {
  // Cross-iframe sync: publish this frame's reference point and adopt a
  // sibling frame's if it is fresher - all same-tab frames then extrapolate
  // from the identical (snapshot, syncedAt) pair. See clockSync.ts.
  if (soccerMatch?.id) {
    publishClockSnapshot(soccerMatch.id, soccerMatch, syncedAt);
    const src = freshestClockSource(soccerMatch.id, soccerMatch, syncedAt);
    soccerMatch = src.match;
    syncedAt = src.syncedAt;
  }

  const md = soccerMatch?.matchDetails;
  const periodId = Number(md?.periodId ?? -1);
  const period = PERIOD_ID[periodId as keyof typeof PERIOD_ID];

  switch (period) {
    case PERIOD_ID_STATUS.HALF_TIME: return "HT";
    case PERIOD_ID_STATUS.FULL_TIME:
    case PERIOD_ID_STATUS.END_OF_2ND_HALF_BEFORE_EXTRA: return "FT";
    case PERIOD_ID_STATUS.EXTRA_TIME_HALF_TIME: return "ET HT";
    // After extra time ends and during the penalty shootout, the match clock
    // freezes at 120:00 — the penalty-shot count renders separately.
    case PERIOD_ID_STATUS.END_OF_EXTRA_TIME_BEFORE_PENALTIES:
    case PERIOD_ID_STATUS.PENALTY_SHOOTOUT: return "120:00";
    case PERIOD_ID_STATUS.PRE_MATCH: return "00:00";
  }

  const apiMin = Math.max(0, Number(md?.matchLengthMin ?? md?.matchTime ?? 0));
  const apiSec = Math.max(0, Number(md?.matchLengthSec ?? 0));
  const max = PERIOD_MAX_MIN[period];

  // Stoppage minutes only ever come from the API (never from local elapsed),
  // so we render `${max}'+${extra}'` whenever Opta reports a minute past max.
  if (max != null && apiMin > max) {
    return `${max}'+${apiMin - max}'`;
  }

  const apiElapsed = syncedAt ? Math.max(0, Math.floor((now - syncedAt) / 1000)) : 0;
  let totalSec = Math.max(
    apiMin * 60 + apiSec + apiElapsed,
    eventsBasedSeconds(soccerMatch?.events, periodId, now),
  );
  // Clamp the local tick at the period max — never push past 45:00/90:00/etc.
  // on our own. Crossing that boundary is the API's job.
  if (max != null) totalSec = Math.min(totalSec, max * 60);

  const minTotal = Math.floor(totalSec / 60);
  const secTotal = totalSec % 60;
  return `${pad(minTotal)}:${pad(secTotal)}`;
}
